import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { MaskLine, Reveal } from "../components/Reveal";
import { BrandMarquee } from "../components/Marquee";
import { EventCard } from "../components/EventCard";
import { JoinTribe } from "../components/Footer";

const CHAPTERS = [
  { no: "01", title: "The cure for the endless scroll", body: "Escape the doomscroll that leaves us more disconnected than ever. The Plus One Tribe is created so you meet real people through shared experiences. No apps, No screens." },
  { no: "02", title: "Curated, not crowded", body: "Every experience is small by design \u2014 we call it a mini tribe. A warm & welcoming host. An experience designed, so nobody stands alone in a corner. We ensure you leave with your mini tribe and not just a number." },
  { no: "03", title: "For every age, across India", body: "Whether you're 18, new to a city or 70 and up for something new, there's a space for you. From Mumbai & Delhi to Bangalore and Goa, we intend to host you thoughtfully. You may walk-in alone but you will walk away with your mini-tribe." },
];

export const Home = () => {
  const [events, setEvents] = useState([]);
  const [meta, setMeta] = useState({ testimonials: [], gallery: [] });
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);

  useEffect(() => {
    api.events({ featured: true }).then((d) => setEvents(d.slice(0, 3))).catch(() => {});
    api.meta().then(setMeta).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
              className="text-xs uppercase tracking-[0.25em] text-terracotta mb-8"
            >
              Real-world experiences &middot; Across India
            </motion.p>
            <h1 className="font-serif font-light text-6xl sm:text-7xl md:text-8xl leading-[0.92] tracking-tighter text-ink">
              <MaskLine delay={0.15}>Find your</MaskLine>
              <MaskLine delay={0.3}><span className="italic text-terracotta">+1</span> in the</MaskLine>
              <MaskLine delay={0.45}>real world.</MaskLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.8 }}
              className="mt-8 text-lg md:text-xl font-light text-muted-earth max-w-xl leading-relaxed"
            >
              The Plus One Tribe curates experiences around &mdash; food, art, hobbies, travel, culture and everything in between. A space to discover something new, meet people who share your curiosity. Leave with a new friend, collaborator, or simply a great story.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.8 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <Link to="/events" data-testid="hero-cta-events" className="group bg-terracotta text-cream px-8 py-4 rounded-full uppercase tracking-[0.15em] text-xs flex items-center gap-2 hover:bg-terracotta-dark transition-colors">
                Explore experiences <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <a href="#manifesto" data-testid="hero-cta-manifesto" className="px-8 py-4 rounded-full uppercase tracking-[0.15em] text-xs border border-ink/25 text-ink hover:border-terracotta hover:text-terracotta transition-colors">
                Why we exist
              </a>
            </motion.div>
          </div>

          <div className="lg:col-span-5">
            <motion.div style={{ y: imgY }} className="relative">
              <motion.div
                initial={{ clipPath: "inset(100% 0 0 0)" }} animate={{ clipPath: "inset(0% 0 0 0)" }}
                transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="overflow-hidden border border-ink/15"
              >
                <img
                  src="https://images.unsplash.com/photo-1625283518288-00362afc8663"
                  alt="Friends gathered on a picnic blanket"
                  className="w-full h-[440px] md:h-[540px] object-cover"
                />
              </motion.div>
              <div className="absolute -bottom-5 -left-5 bg-ochre text-ink px-6 py-4 border border-ink/15 hidden md:block">
                <p className="font-serif text-3xl leading-none">12&ndash;30</p>
                <p className="text-[10px] uppercase tracking-[0.15em] mt-1">people per table</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <BrandMarquee />

      {/* MANIFESTO */}
      <section id="manifesto" data-testid="manifesto" className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.2em] text-terracotta mb-4">Why we exist</p>
          <h2 className="font-serif font-light text-4xl md:text-6xl tracking-tight text-ink max-w-3xl leading-tight">
            Connection isn&rsquo;t found on a screen, it&rsquo;s created in person through shared experiences.
          </h2>
        </Reveal>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.no} delay={i * 0.12}>
              <div className="border-t-2 border-terracotta pt-6">
                <span className="font-serif text-5xl text-ink/20">{c.no}</span>
                <h3 className="font-serif text-2xl md:text-3xl text-ink mt-4 mb-3 leading-snug">{c.title}</h3>
                <p className="text-muted-earth font-light leading-relaxed">{c.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FEATURED EVENTS */}
      <section data-testid="featured-events" className="bg-[hsl(40_25%_92%)] border-y border-ink/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-terracotta mb-4">This season</p>
                <h2 className="font-serif font-light text-4xl md:text-6xl tracking-tight text-ink">Come Along</h2>
              </div>
              <Link to="/events" data-testid="view-all-events" className="text-xs uppercase tracking-[0.15em] text-ink flex items-center gap-2 hover:text-terracotta transition-colors">
                View all cities <ArrowRight size={15} />
              </Link>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 gap-6 md:gap-8">
            {events.map((e, i) => (
              <Reveal key={e.id} delay={i * 0.08}>
                <EventCard event={e} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section data-testid="gallery" className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.2em] text-terracotta mb-4">The tribe, in the wild</p>
          <h2 className="font-serif font-light text-4xl md:text-6xl tracking-tight text-ink mb-14">Moments from past gatherings</h2>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-12 gap-4 md:gap-6 auto-rows-[180px] md:auto-rows-[220px]">
          {meta.gallery.slice(0, 6).map((src, i) => {
            const spans = ["md:col-span-8 md:row-span-2", "md:col-span-4", "md:col-span-4", "md:col-span-5", "md:col-span-7", "md:col-span-12"];
            return (
              <Reveal key={i} delay={i * 0.06} className={`overflow-hidden border border-ink/15 ${spans[i] || "md:col-span-4"}`}>
                <div className="w-full h-full overflow-hidden group">
                  <img src={src} alt="Past gathering" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[900ms]" />
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section data-testid="testimonials" className="bg-ochre border-y border-ink/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <p className="text-xs uppercase tracking-[0.2em] text-ink/60 mb-14">People who found their +1</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {meta.testimonials.map((t, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <blockquote className="border-l-2 border-ink/30 pl-6">
                  <p className="font-serif italic text-2xl md:text-3xl text-ink leading-snug">&ldquo;{t.quote}&rdquo;</p>
                  <footer className="mt-6">
                    <p className="text-sm font-medium text-ink">{t.name}</p>
                    <p className="text-xs uppercase tracking-[0.15em] text-ink/60 mt-1">{t.meta}</p>
                  </footer>
                </blockquote>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <JoinTribe />
    </div>
  );
};
